<template>
  <c-box mt="4xl">
    <main
      id="content"
      v-chakra
      :mt="{ base: 'xl', sm: '4xl', md: 0 }"
      :mx="{ base: '-16px', sm: 0 }"
    >
      <c-box :my="{ base: 'md', md: '3.5rem' }">
        <c-heading
          as="h1"
          :font-size="{ base: '2xl', md: '4xl' }"
          mt="md"
          mb="xs"
          color="brand.purpleDark"
          font-weight="normal"
        >
          {{ $t("productReturn.displaySingle.oldReturn") }}
        </c-heading>
        <c-heading as="h2" font-size="md" color="deepBlack" font-weight="normal">
          {{ displayId }}
        </c-heading>
      </c-box>

      <c-divider width="100%" border-color="gray.300" :mb="{ base: 'md', md: '3xl', lg: '4xl' }" />

      <c-flex v-if="isLoading" width="100%" justify-content="center">
        <Spinner />
      </c-flex>

      <c-box v-else-if="returnData.created">
        <c-text font-size="sm" :mb="{ base: 'md', md: '3xl' }">
          * {{ $t("productReturn.displaySingle.oldReturnNote") }}
        </c-text>
        <c-flex
          :gap="{ base: '2', md: '6', lg: '12', xl: '16' }"
          :flex-direction="{ base: 'column', md: 'row' }"
        >
          <c-grid
            :template-columns="{ md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }"
            :columnGap="{ base: '6', lg: '12', xl: '16' }"
            :rowGap="{ base: '6', sm: '10' }"
            :mb="{ base: 'md', md: '3xl', lg: '4xl' }"
          >
            <!-- submitted by -->
            <c-grid-item>
              <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
                {{ $t("productReturn.displaySingle.submitted") }}
              </c-heading>
              <c-text font-weight="350">
                {{
                  $t("productReturn.displaySingle.submittedBy", {
                    date: moment(returnData.created).format("D.M.YYYY"),
                    creator: `${user.firstName} ${user.lastName}`,
                  })
                }}
              </c-text>
            </c-grid-item>

            <!-- status -->
            <c-grid-item>
              <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
                {{ $t("productReturn.displaySingle.overallStatus") }}
              </c-heading>
              <c-box font-weight="350">
                <c-box
                  mb="sm"
                  v-chakra="{
                    '.return-progress > div': {
                      backgroundColor: 'brand.purpleLight',
                    },
                  }"
                >
                  <c-progress
                    class="return-progress"
                    has-stripe
                    :value="100"
                    border-radius="full"
                    size="lg"
                    background="gray.200"
                    width="100%"
                    min-width="5rem"
                    max-width="12rem"
                  />
                </c-box>
                <c-text font-weight="350">
                  {{ $t("productReturn.note.oldReturnStatusesMissing") }}
                </c-text>
              </c-box>
            </c-grid-item>
          </c-grid>

          <c-box v-if="returnData.shipperType !== 'MANUAL'" :width="{ md: '33.333%', xl: '25%' }">
            <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
              {{ $t("productReturn.displaySingle.printParcelLabel") }}
            </c-heading>
            <c-box>
              <!-- Use SALES_RETURN for old returns since it uses the older label endpoint -->
              <RMADownloadParcel
                :return-id="returnData.rmaId ?? ''"
                return-type="SALES_RETURN"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
              <template v-if="returnData.parcels">
                <RMAParcelTrackingLink
                  :rma-id="returnData.rmaId ?? ''"
                  link-type="link"
                  v-chakra
                  :width="{ md: '100%' }"
                  max-width="20rem"
                  mb="xs"
                  display="block"
                />
              </template>
            </c-box>
          </c-box>
        </c-flex>
      </c-box>
    </main>
  </c-box>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import RMADownloadParcel from "@/views/rma/RMADownloadParcel.vue";
import RMAProcessSteps from "@/views/rma/RMAProcessSteps.vue";
import RMAParcelTrackingLink from "@/views/rma/RMAParcelTrackingLink.vue";
import { IResultEntity, IRootState } from "@iloq/shared";
import Spinner from "@/components/Spinner.vue";
import {
  CBox,
  CDivider,
  CFlex,
  CGrid,
  CGridItem,
  CHeading,
  CProgress,
  CText,
} from "@chakra-ui/vue";

interface IData {
  isLoading: boolean;
  returnData: IResultEntity | Record<string, never>;
  displayId?: string;
  moment: any;
}

export default defineComponent({
  name: "OldReturnDetails",
  components: {
    RMADownloadParcel,
    RMAParcelTrackingLink,
    RMAProcessSteps,
    CBox,
    CHeading,
    CDivider,
    CFlex,
    CText,
    CGrid,
    CGridItem,
    CProgress,
    Spinner,
  },
  props: {
    rmaId: {
      type: String,
      required: true,
    },
  },
  data(): IData {
    return {
      isLoading: false,
      returnData: {},
      displayId: "",
      moment: (this as any).$moment,
    };
  },
  computed: mapState({
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    returnListing: (state: unknown) => (state as IRootState).returns!.list,
  }),
  mounted(): void {
    this.syncReturnData(this.rmaId);
  },
  methods: {
    syncReturnData(rmaId: string): void {
      this.isLoading = true;
      this.$store.watch(
        (state: any) => state.returns.list.find((item: IResultEntity) => item.rmaId === rmaId),
        (newValue: any) => {
          this.updateReturnData(newValue);
        },
      );

      const returnData = this.returnListing.find((item: IResultEntity) => item.rmaId === rmaId);
      if (returnData) {
        this.updateReturnData(returnData);
      }
    },
    updateReturnData(returnData: IResultEntity): void {
      this.returnData = returnData;
      this.displayId = returnData.rmaId?.substring(0, 7);
      this.isLoading = false;
    },
  },
});
</script>
